import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * Silverado: barra de envío gratis.
 *
 * El estado inicial llega pintado desde Liquid (snippets/sk9-free-shipping-bar.liquid).
 * Este componente:
 *  - actualiza la barra en vivo con el evento estándar de carrito (`StandardEvents.cartLinesUpdate`);
 *  - reacciona cuando el carrito se vuelve a renderizar (morph cambia `data-total`);
 *  - lanza confeti solo cuando el total CRUZA el monto (de abajo hacia arriba), una vez por cruce.
 *    El último total visto se guarda en sessionStorage para no repetir el confeti en cada página.
 *  - si la barra está oculta (carrito lateral cerrado) espera a que sea visible para lanzar el confeti.
 */

const STORAGE_KEY = 'sk9-fsb-last-total';

/** @param {string} value */
function escapeHtml(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function readLastTotal() {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY);
    return value === null ? null : Number(value);
  } catch (_) {
    return null;
  }
}

/** @param {number} cents */
function writeLastTotal(cents) {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(cents));
  } catch (_) {
    // sin almacenamiento disponible: no pasa nada
  }
}

class Sk9FreeShippingBar extends HTMLElement {
  static observedAttributes = ['data-total'];

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  get threshold() {
    return Number(this.dataset.threshold) || 0;
  }

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, {
      signal: this.#abort.signal,
    });

    this.#evaluate(Number(this.dataset.total) || 0);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
  }

  /**
   * El carrito se volvió a renderizar desde el servidor (morph) con un total nuevo.
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue || !this.isConnected) return;
    this.#evaluate(Number(newValue) || 0);
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        if (detail?.didError) return;
        const amount = cart?.cost?.totalAmount?.amount;
        if (amount === undefined || amount === null) return;

        const cents = Math.round(parseFloat(amount) * 100);
        if (Number.isNaN(cents)) return;

        this.#render(cents);
        this.#evaluate(cents);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sk9-free-shipping-bar]', error);
      });
  };

  /**
   * Pinta la barra para un total dado (en centavos).
   * @param {number} cents
   */
  #render(cents) {
    const threshold = this.threshold;
    if (!threshold) return;

    const percent = Math.min(100, Math.floor((cents * 100) / threshold));
    const reached = cents >= threshold;

    this.style.setProperty('--sk9-fsb-progress', `${percent}%`);
    this.classList.toggle('sk9-fsb--success', reached);

    const track = this.querySelector('[data-sk9-fsb-track]');
    track?.setAttribute('aria-valuenow', String(percent));

    const pct = this.querySelector('[data-sk9-fsb-pct]');
    if (pct) pct.textContent = `${percent}%`;

    const message = this.querySelector('[data-sk9-fsb-message]');
    if (!message) return;

    if (reached) {
      message.innerHTML = escapeHtml(this.dataset.textSuccess || '');
    } else {
      const remaining = formatMoney(
        threshold - cents,
        this.dataset.moneyFormat || '${{amount}}',
        this.dataset.currency || 'MXN'
      );
      message.innerHTML = escapeHtml(this.dataset.textRemaining || '').replace(
        '[monto]',
        `<strong>${escapeHtml(remaining)}</strong>`
      );
    }
  }

  /**
   * Decide si hay que celebrar: solo cuando el total pasa de abajo del monto a arriba.
   * @param {number} cents
   */
  #evaluate(cents) {
    const threshold = this.threshold;
    if (!threshold) return;

    const last = readLastTotal();
    writeLastTotal(cents);

    const crossed = cents >= threshold && (last === null || last < threshold);
    if (crossed) this.#celebrateWhenVisible();
  }

  #celebrateWhenVisible() {
    if (this.dataset.confetti !== 'true') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#visibilityObserver?.disconnect();
        this.#visibilityObserver = null;
        // Pequeña pausa para que el carrito lateral termine de abrir.
        setTimeout(() => launchConfetti(this, this.#colors()), 200);
      },
      { threshold: 0.4 }
    );
    this.#visibilityObserver.observe(this);
  }

  #colors() {
    const colors = (this.dataset.confettiColors || '')
      .split(',')
      .map((color) => color.trim())
      .filter(Boolean);
    return colors.length ? colors : ['#c17a1b', '#ffb81c', '#5f6342'];
  }
}

/**
 * Lanza confeti desde el centro de un elemento. Usa un canvas en popover para quedar
 * por encima de diálogos modales (carrito lateral). No bloquea clics.
 * @param {HTMLElement} origin
 * @param {string[]} colors
 */
function launchConfetti(origin, colors) {
  const canvas = document.createElement('canvas');
  canvas.className = 'sk9-confetti';
  canvas.setAttribute('aria-hidden', 'true');

  let usePopover = typeof canvas.showPopover === 'function';
  if (usePopover) canvas.setAttribute('popover', 'manual');

  document.body.append(canvas);

  if (usePopover) {
    try {
      canvas.showPopover();
    } catch (_) {
      usePopover = false;
    }
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    canvas.remove();
    return;
  }

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const width = window.innerWidth;
  const height = window.innerHeight;
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  ctx.scale(dpr, dpr);

  const rect = origin.getBoundingClientRect();
  const originX = rect.left + rect.width / 2;
  const originY = rect.top + rect.height / 2;
  const count = width < 750 ? 90 : 150;

  const particles = Array.from({ length: count }, (_, index) => {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.95;
    const speed = 7 + Math.random() * 10;
    return {
      x: originX + (Math.random() - 0.5) * rect.width * 0.6,
      y: originY,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 6 + Math.random() * 6,
      rotation: Math.random() * Math.PI,
      spin: (Math.random() - 0.5) * 0.3,
      tilt: Math.random() * Math.PI,
      color: colors[index % colors.length],
      round: Math.random() < 0.3,
    };
  });

  const duration = 2800;
  const start = performance.now();

  /** @param {number} now */
  const frame = (now) => {
    const elapsed = now - start;
    const fade = elapsed > duration - 700 ? Math.max(0, (duration - elapsed) / 700) : 1;

    ctx.clearRect(0, 0, width, height);

    for (const p of particles) {
      p.vx *= 0.985;
      p.vy = p.vy * 0.985 + 0.32;
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.spin;
      p.tilt += 0.12;

      ctx.save();
      ctx.globalAlpha = fade;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);
      ctx.fillStyle = p.color;

      if (p.round) {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2.6, 0, Math.PI * 2);
        ctx.fill();
      } else {
        const squash = Math.abs(Math.cos(p.tilt));
        ctx.fillRect(-p.size / 2, (-p.size * 0.3 * squash) / 1, p.size, p.size * 0.6 * squash + 1);
      }

      ctx.restore();
    }

    if (elapsed < duration) {
      requestAnimationFrame(frame);
    } else {
      if (usePopover) {
        try {
          canvas.hidePopover();
        } catch (_) {
          // ya estaba cerrado
        }
      }
      canvas.remove();
    }
  };

  requestAnimationFrame(frame);
}

if (!customElements.get('sk9-free-shipping-bar')) {
  customElements.define('sk9-free-shipping-bar', Sk9FreeShippingBar);
}
